<template>
  <Menu
    as="div"
    v-slot="{ close }"
    class="relative profile-menu mx-[8px] md:mx-[10px] lg:mx-[12px] ltr:first:ml-0 ltr:last:mr-0 rtl:first:mr-0 rtl:last:ml-0"
  >
    <MenuButton
      class="flex items-center -mx-[5px] relative ltr:pr-[14px] rtl:pl-[14px] text-black dark:text-whit"
    >
      <img
        :src="profilePicture"
        class="w-[35px] h-[35px] md:w-[42px] md:h-[42px] rounded-full ltr:md:mr-[2px] ltr:lg:mr-[8px] rtl:md:ml-[2px] rtl:lg:ml-[8px] border-[2px] border-primary-200 inline-block"
        alt="user-profile-image"
      />
      <span class="block font-semibold text-[0] lg:text-base"> {{ userName }} </span>
      <i
        class="ri-arrow-down-s-line text-[15px] absolute ltr:-right-[3px] rtl:-left-[3px] top-1/2 -translate-y-1/2 mt-px"
      ></i>
    </MenuButton>

    <transition
      enter-active-class="transition ease-out duration-100"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
    >
      <MenuItems
        class="bg-white dark:bg-[#0c1427] transition-all shadow-3xl dark:shadow-none py-[22px] absolute mt-[13px] md:mt-[14px] w-[195px] z-[1] top-full ltr:right-0 rtl:left-0 rounded-md focus:outline-hidden"
      >
        <div
          class="flex items-center border-b border-gray-100 dark:border-[#172036] pb-[12px] mx-[20px] mb-[10px]"
        >
          <img
            :src="profilePicture"
            class="rounded-full w-[31px] h-[31px] ltr:mr-[9px] rtl:ml-[9px] border-2 border-primary-200 inline-block"
            alt="user-profile-image"
          />
          <div>
            <span class="block text-black dark:text-white font-medium">
              {{ userFullName }}
            </span>
            <span class="block text-xs"> {{ userRole }} </span>
          </div>
        </div>

        <ul>
          <li>
            <RouterLink
              to="/apps/chat"
              class="block relative py-[7px] ltr:pl-[50px] ltr:pr-[20px] rtl:pr-[50px] rtl:pl-[20px] text-black dark:text-white transition-all hover:text-primary-500"
              exact
              @click="close()"
            >
              <i
                class="material-symbols-outlined top-1/2 -translate-y-1/2 !text-[22px] absolute ltr:left-[20px] rtl:right-[20px]"
              >
                chat
              </i>
              {{ directT('navbar.messages') }}
            </RouterLink>
          </li>
          <li>
            <RouterLink
              to="/apps/to-do-list"
              class="block relative py-[7px] ltr:pl-[50px] ltr:pr-[20px] rtl:pr-[50px] rtl:pl-[20px] text-black dark:text-white transition-all hover:text-primary-500"
              exact
              @click="close()"
            >
              <i
                class="material-symbols-outlined top-1/2 -translate-y-1/2 !text-[22px] absolute ltr:left-[20px] rtl:right-[20px]"
              >
                format_list_bulleted
              </i>
              {{ directT('navbar.myTask') }}
            </RouterLink>
          </li>
        </ul>

        <div
          class="border-t border-gray-100 dark:border-[#172036] mx-[20px] my-[9px]"
        ></div>

        <ul>
          <li>
            <RouterLink
              to="/settings/account-settings"
              class="block relative py-[7px] ltr:pl-[50px] ltr:pr-[20px] rtl:pr-[50px] rtl:pl-[20px] text-black dark:text-white transition-all hover:text-primary-500"
              exact
              @click="close()"
            >
              <i
                class="material-symbols-outlined top-1/2 -translate-y-1/2 !text-[22px] absolute ltr:left-[20px] rtl:right-[20px]"
              >
                settings
              </i>
              {{ directT('common.settings') }}
            </RouterLink>
          </li>
          <li>
            <RouterLink
              to="/authentication/lock-screen"
              class="block relative py-[7px] ltr:pl-[50px] ltr:pr-[20px] rtl:pr-[50px] rtl:pl-[20px] text-black dark:text-white transition-all hover:text-primary-500"
              exact
              @click="handleLockScreen(); close()"
            >
              <i
                class="material-symbols-outlined top-1/2 -translate-y-1/2 !text-[22px] absolute ltr:left-[20px] rtl:right-[20px]"
              >
                lock
              </i>
              {{ directT('navbar.lockScreen') }}
            </RouterLink>
          </li>
          <li>
            <RouterLink
              to="/authentication/logout"
              class="block relative py-[7px] ltr:pl-[50px] ltr:pr-[20px] rtl:pr-[50px] rtl:pl-[20px] text-red-500 transition-all hover:text-red-700 hover:bg-red-50 dark:hover:bg-[#2A1515]"
              exact
              style="color: #ff0000 !important;"
              @click="close()"
            >
              <i
                class="material-symbols-outlined top-1/2 -translate-y-1/2 !text-[22px] absolute ltr:left-[20px] rtl:right-[20px]"
                style="color: #ff0000 !important;"
              >
                logout
              </i>
              {{ directT('common.logout') }}
            </RouterLink>
          </li>
        </ul>
      </MenuItems>
    </transition>
  </Menu>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed } from "vue";
import { Menu, MenuButton, MenuItems } from "@headlessui/vue";
import { supabase } from "@/lib/supabaseClient";
import defaultProfileImage from "@/assets/images/admin.png";
import { useI18n } from "vue-i18n";

// Direct import of translation files for manual translation lookup
import enTranslations from '@/i18n/locales/en.json';
import heTranslations from '@/i18n/locales/he.json';
import arTranslations from '@/i18n/locales/ar.json';

// Create type for translation object structure
type TranslationObject = Record<string, any>;

// Define the interface for user profile
interface UserProfile {
  id: string;
  user_id: string;
  display_name: string;
  role: string;
  email: string;
  profile_picture_link?: string;
}

export default defineComponent({
  name: "AdminProfile",
  components: {
    Menu,
    MenuButton,
    MenuItems
  },
  setup() {
    const { t, locale } = useI18n();
    const userName = ref('');
    const userFullName = ref('');
    const userRole = ref('User');
    const profilePicture = ref('@/assets/images/admin.png');
    
    // Store direct references to translation files
    const translationFiles = {
      en: enTranslations,
      he: heTranslations,
      ar: arTranslations
    };

    // Direct translation function that bypasses vue-i18n
    const directT = (key: string) => {
      const parts = key.split('.');
      let result: TranslationObject = translationFiles[locale.value as keyof typeof translationFiles];
      
      for (const part of parts) {
        if (!result || !(part in result)) {
          console.warn(`Missing translation key: ${key} in ${locale.value} locale`);
          // Fallback to English if translation not found
          let enResult: TranslationObject = translationFiles.en;
          for (const fallbackPart of parts) {
            if (!enResult || !(fallbackPart in enResult)) return key;
            enResult = enResult[fallbackPart as keyof typeof enResult] as TranslationObject;
          }
          return enResult;
        }
        result = result[part as keyof typeof result] as TranslationObject;
      }
      return result;
    };
    
    onMounted(async () => {
      try {
        // Get current session
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session && session.user) {
          // Get user email from auth
          const email = session.user.email || '';
          
          // Get user profile data from user_profiles table
          const { data, error } = await supabase
            .from('user_profiles')
            .select('id, user_id, display_name, role, email, profile_picture_link')
            .eq('user_id', session.user.id)
            .single();
          
          if (error) {
            console.error('Error fetching user profile:', error);
            // Fallback to email
            userName.value = email.split('@')[0] || 'User';
            userFullName.value = email || 'User';
            return;
          }
          
          const profile = data as UserProfile;
          
          if (profile && profile.display_name) {
            // Use display_name for both userName and userFullName
            userName.value = profile.display_name;
            userFullName.value = profile.display_name;
            userRole.value = profile.role || 'User';
            
            // Set profile picture if available
            if (profile.profile_picture_link && profile.profile_picture_link.trim() !== '') {
              profilePicture.value = profile.profile_picture_link;
            } else {
              // Keep using the default image from assets when no profile picture is set
              profilePicture.value = defaultProfileImage;
            }
          } else {
            // Fallback to email
            userName.value = email.split('@')[0] || 'User';
            userFullName.value = email || 'User';
          }
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
        userName.value = 'User';
        userFullName.value = 'User';
      }
    });

    const handleLockScreen = () => {
      // Store the current path in localStorage before navigating to the lock screen
      localStorage.setItem('previousPath', window.location.pathname);
    };

    return {
      userName,
      userFullName,
      userRole,
      profilePicture,
      handleLockScreen,
      directT
    };
  }
});
</script>
